/**
 * sirus/CastBar — R232q21 Phase 4 (Sirus 1:1 UI rebuild).
 *
 * The 240×24 cast / channel bar shown 16px above the bottom-centre
 * ActionBar. Visual states:
 *   – cast: bar fills LEFT → RIGHT, gold gradient.
 *   – channel: bar drains RIGHT → LEFT, teal gradient.
 *   – interrupt: 320ms red shake + fade-out.
 *
 * Source of truth:
 *   `useAbilityCD.activeCast` (id, name, startedAt, durationMs)
 *
 * The current ability-cd-store doesn't expose a `casting` envelope nor a
 * channel/interrupt flag. To stay schema-flexible we read the store
 * loosely: an extended cast row may carry `channel?: boolean` /
 * `interrupted?: boolean`. If neither is present we render in pure cast
 * mode. The interrupt animation is gated by a local React state that we
 * raise when the active cast disappears suspiciously early (< 50% of its
 * declared duration). This is a heuristic; once the server pipes a real
 * interrupt event into the store we'll prefer the explicit flag.
 *
 * Position: fixed bottom-centre, 16px above ActionBar (which lives at
 * bottom: 16, max ~80px high → CastBar bottom ≈ 110).
 * z-index 30.
 *
 * DEFERRED:
 *   – Real subscription to a server `cast-interrupt` event (currently
 *     inferred from "active cast vanished early").
 *   – Channel mode flag — relies on the optional `channel` boolean we
 *     read off the cast object, never set anywhere yet.
 *   – Spell-icon art: while the placeholder renders the first character
 *     of the spell name as a glyph, hot-wired DBC icon URLs come later.
 *   – Mobile-friendly relocation: we just translate-up so it doesn't
 *     overlap the on-screen joystick. A dedicated mobile stack is owned
 *     by the parent layout.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useAbilityCD } from '../../state/ability-cd-store.js';

/** Loose extension that lets us peek at `channel` / `interrupted` if a
 *  later store revision adds them — current store has none. */
type ExtCast = {
  id: string;
  name: string;
  startedAt: number;
  durationMs: number;
  channel?: boolean;
  interrupted?: boolean;
};

/**
 * Bar dimensions. BAR_W stays a fixed pixel literal because the cast
 * bar's width is calibrated to fit the action-bar centre region; height
 * + icon size flow from rubber-UI tokens so they scale with the player
 * frame on tiny phones / 4K monitors.
 */
const BAR_W = 240;
const BAR_H = 'calc(var(--aiwari-bar-h) * 1.5)';
const ICON_PX = 'calc(var(--aiwari-bar-h) * 1.5)';
const SHAKE_MS = 320;

/** Glyph fallback when no icon URL is available — first character of the
 *  spell's Russian name, or a sword emoji. */
function fallbackGlyph(name: string): string {
  if (!name) return '⚔';
  return name.trim().charAt(0).toUpperCase();
}

export function CastBar() {
  const activeCastRaw = useAbilityCD((s) => s.activeCast) as unknown as ExtCast | null;

  // Tick at 50ms so the bar smoothly animates without re-rendering the
  // whole HUD.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!activeCastRaw) return;
    const id = window.setInterval(() => setTick((t) => (t + 1) & 0xffff), 50);
    return () => window.clearInterval(id);
  }, [activeCastRaw]);

  // Track interrupt heuristically: when activeCast disappears before
  // its expected end time, raise a 320ms shake/fade.
  const lastCast = useRef<ExtCast | null>(null);
  const [interrupted, setInterrupted] = useState<{ name: string; firedAt: number } | null>(null);

  useEffect(() => {
    const prev = lastCast.current;
    if (activeCastRaw) {
      lastCast.current = activeCastRaw;
      return;
    }
    if (!prev) return;
    const expectedEnd = prev.startedAt + prev.durationMs;
    const now = performance.now();
    if (now + 60 < expectedEnd) {
      setInterrupted({ name: prev.name, firedAt: now });
      window.setTimeout(() => {
        setInterrupted((cur) => (cur && performance.now() - cur.firedAt > SHAKE_MS ? null : cur));
      }, SHAKE_MS + 20);
    }
    lastCast.current = null;
  }, [activeCastRaw]);

  const view = useMemo(() => {
    if (!activeCastRaw) return null;
    const elapsed = Math.max(0, performance.now() - activeCastRaw.startedAt);
    const total = Math.max(1, activeCastRaw.durationMs);
    const fraction = Math.min(1, elapsed / total);
    return {
      id: activeCastRaw.id,
      name: activeCastRaw.name,
      remainingMs: Math.max(0, total - elapsed),
      fraction,
      total,
      channel: !!activeCastRaw.channel,
    };
  }, [activeCastRaw]);

  // No active cast and no recent interrupt → don't render anything at all.
  if (!view && !interrupted) return null;

  const remainingS = view ? (view.remainingMs / 1000).toFixed(1) : '0.0';
  const fillPct = view
    ? view.channel
      ? (1 - view.fraction) * 100
      : view.fraction * 100
    : 0;

  const fill = view?.channel
    ? 'linear-gradient(90deg, hsl(160 50% 30%) 0%, hsl(160 70% 55%) 100%)'
    : 'linear-gradient(90deg, var(--aiwari-gold-dark) 0%, var(--aiwari-gold) 100%)';

  const showInterrupt = !!interrupted;
  const interruptOpacity = showInterrupt
    ? Math.max(0, 1 - (performance.now() - (interrupted?.firedAt ?? 0)) / SHAKE_MS)
    : 0;

  return (
    <div
      data-aiwari-cast-bar
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 110,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 30,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        pointerEvents: 'none',
        animation: showInterrupt ? `aiwari-cast-shake ${SHAKE_MS}ms ease-out` : undefined,
      }}
    >
      <style>{`
        @keyframes aiwari-cast-shake {
          0%   { transform: translateX(calc(-50% - 4px)); }
          25%  { transform: translateX(calc(-50% + 4px)); }
          50%  { transform: translateX(calc(-50% - 3px)); }
          75%  { transform: translateX(calc(-50% + 3px)); }
          100% { transform: translateX(-50%); }
        }
      `}</style>

      {/* ── Spell icon ───────────────────────────────────────── */}
      <div
        aria-hidden
        style={{
          width: ICON_PX,
          height: ICON_PX,
          background: 'var(--aiwari-frame-stone)',
          border: '1px solid var(--aiwari-frame-edge)',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--aiwari-font-display)',
          fontSize: 'var(--aiwari-text-md)',
          color: 'var(--aiwari-gold-soft)',
          textShadow: '0 1px 0 #000',
          flex: '0 0 auto',
        }}
      >
        {view ? fallbackGlyph(view.name) : '!'}
      </div>

      {/* ── Bar track ────────────────────────────────────────── */}
      <div
        style={{
          position: 'relative',
          width: BAR_W,
          height: BAR_H,
          background: 'hsl(0 0% 0% / 0.7)',
          border: `1px solid ${showInterrupt ? 'hsl(0 70% 50%)' : 'var(--aiwari-frame-edge)'}`,
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: showInterrupt
            ? `inset 0 0 12px hsl(0 70% 50% / ${0.35 + interruptOpacity * 0.4})`
            : '0 0 8px hsl(44 100% 49% / 0.25)',
        }}
      >
        {/* Filled portion */}
        {view && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: view.channel ? `${100 - fillPct}%` : 0,
              width: `${fillPct}%`,
              background: fill,
              transition: 'width 50ms linear, left 50ms linear',
              boxShadow: 'inset 0 1px 0 rgba(255, 220, 140, 0.35)',
            }}
          />
        )}

        {/* Red interrupt overlay — fades over SHAKE_MS. */}
        {showInterrupt && (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              background: `hsl(0 70% 35% / ${0.55 * interruptOpacity})`,
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Label + remaining time */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 8px',
            fontFamily: 'var(--aiwari-font-display)',
            fontSize: 'var(--aiwari-text-md)',
            color: 'var(--aiwari-fg)',
            textShadow: '0 1px 2px #000',
            letterSpacing: 0.5,
            pointerEvents: 'none',
          }}
        >
          <span
            style={{
              flex: 1,
              minWidth: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {showInterrupt ? 'Прервано' : view?.name ?? ''}
          </span>
          {view && (
            <span
              style={{
                fontFamily: 'var(--aiwari-font-mono)',
                fontSize: 'var(--aiwari-text-sm)',
                color: 'var(--aiwari-gold-soft)',
                marginLeft: 6,
              }}
            >
              {remainingS}с
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default CastBar;
